// src/app/core/services/summary-service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { ProductService, CatalogItem } from './product-service';
import { DomainSelection } from './payload-service';
import { sortByO365AddonGroups, groupO365SummaryAddonLines, O365AddonGroup } from '../utils/o365-addon-groups';

export interface SummaryLine {
  id: string;
  offerId?: string;
  xmlAttributeName?: string;
  name: string;
  kind: 'main' | 'addon' | 'domain' | 'o365';
  unitPrice: number;
  quantity: number;
  total: number;
  description?: string;
  billingCycle?: 'monthly' | 'annual';
  commitment?: 'monthly' | 'annual';
  parentProductId?: string;
  /** O365 cart row id — used for edit/remove in summary. */
  cartLineId?: string;
  /** Catalog addon product id for O365 cart add-on rows. */
  addonProductId?: string;
}

export interface O365CartAddon {
  addonProductId: string;
  quantity: number;
}

export interface O365CartLine {
  lineId: string;
  catalogProductId: string;
  commitment: 'monthly' | 'annual';
  quantity: number;
  addons: O365CartAddon[];
}

export interface SummaryResult {
  quantity: number;
  items: SummaryLine[];
  upgradeSelected: boolean;
  totalMonthly: number;
}

export interface OrderItem {
  offeringId: string;
  productId: string;
  quantity: string;
  domainName?: string;
}

const DOMAIN_PRODUCT_ID = 'DOMAIN_REGISTRATION';

/** Maximum O365 license quantity allowed per account/signup request. */
export const O365_MAX_LICENSES = 99;

/** Minimum monthly subscription charge for O365 when only monthly items are in cart. */
export const O365_MIN_MONTHLY_SPEND = 10;

function normalizeO365CartAddons(addons: O365CartAddon[]): O365CartAddon[] {
  return [...addons]
    .sort((left, right) => left.addonProductId.localeCompare(right.addonProductId))
    .map((addon) => ({
      addonProductId: addon.addonProductId,
      quantity: Math.max(1, Number(addon.quantity) || 1),
    }));
}

function o365CartLineSignature(
  catalogProductId: string,
  commitment: 'monthly' | 'annual',
  addons: O365CartAddon[]
): string {
  const addonKey = normalizeO365CartAddons(addons)
    .map((addon) => `${addon.addonProductId}:${addon.quantity}`)
    .join('|');
  return `${catalogProductId}::${commitment}::${addonKey}`;
}

function getO365CartLineAddonQuantityTotal(line: O365CartLine): number {
  return line.addons.reduce((sum, addon) => sum + addon.quantity, 0);
}

function getO365CartLineLicenseQuantity(line: O365CartLine): number {
  return line.quantity + getO365CartLineAddonQuantityTotal(line);
}

function getO365CartLicenseQuantity(
  productQuantity: number,
  addons: O365CartAddon[] = []
): number {
  const q = Math.max(1, Number(productQuantity) || 1);
  const normalizedAddons = normalizeO365CartAddons(addons).map((addon) => ({
    ...addon,
    quantity: Math.min(addon.quantity, q),
  }));

  return q + normalizedAddons.reduce((sum, addon) => sum + addon.quantity, 0);
}

function o365AddonKey(parentProductId: string, addonProductId: string): string {
  return `${parentProductId}::${addonProductId}`;
}

@Injectable({ providedIn: 'root' })
export class SummaryService {
  private catalogById = new Map<string, CatalogItem>();
  /** Maps addon productId -> base productIds that expose that addon in the catalog. */
  private addonSupportedBy = new Map<string, Set<string>>();
  /** Maps aggregate addon key -> base productIds that expose a matching addon offer. */
  private addonAggregateSupportedBy = new Map<string, Set<string>>();
  /** Maps aggregate addon key -> addon productIds grouped under the same offer. */
  private addonProductIdsByAggregateKey = new Map<string, Set<string>>();
  private invalidQuantities = new Map<string, boolean>();

  private selectedAddons$ = new BehaviorSubject<Set<string>>(new Set());
  private selectedProducts$ = new BehaviorSubject<Set<string>>(new Set());
  private quantities$ = new BehaviorSubject<Map<string, number>>(new Map());
  private commitmentSelections$ = new BehaviorSubject<Map<string, 'monthly' | 'annual'>>(
    new Map()
  );
  private o365CartLines$ = new BehaviorSubject<O365CartLine[]>([]);
  private o365LineCounter = 0;
  private domain$ = new BehaviorSubject<DomainSelection | null>(null);
  private ready$ = new BehaviorSubject<boolean>(false);

  summary$!: Observable<SummaryResult>;
  orderItems$!: Observable<OrderItem[]>;

  constructor(private productService: ProductService) {
    combineLatest([
      this.productService.rackspaceProducts$,
      this.productService.rackspaceAddons$,
      this.productService.office365Products$,
      this.productService.office365Addons$
    ]).subscribe(([rackProds, rackAddons, o365Prods, o365Addons]) => {
      this.catalogById.clear();
      this.addonSupportedBy.clear();
      this.addonAggregateSupportedBy.clear();
      this.addonProductIdsByAggregateKey.clear();
      [...rackProds, ...rackAddons, ...o365Prods].forEach((p) =>
        this.catalogById.set(p.productId, p)
      );
      rackAddons.forEach((addon) => {
        if (!this.addonSupportedBy.has(addon.productId)) {
          this.addonSupportedBy.set(addon.productId, new Set());
        }
        rackProds.forEach((prod) => {
          this.addonSupportedBy.get(addon.productId)!.add(prod.productId);
        });
      });
      o365Prods.forEach((product) => {
        this.registerO365ProductAddons(product);
        if (product.annualVariant) {
          this.registerO365ProductAddons(product.annualVariant, product.productId);
        }
      });
      // Fallback for flattened addon list (same keys as nested)
      o365Addons.forEach((addon) => {
        if (addon.parentProductId) {
          this.catalogById.set(o365AddonKey(addon.parentProductId, addon.productId), addon);
          this.registerO365AddonSupport(addon, addon.parentProductId);
        }
      });
      this.ready$.next(this.catalogById.size > 0);
    });

    this.selectedProducts$.subscribe((products) => {
      this.pruneOrphanedAddons(products);
    });

    this.summary$ = combineLatest([
      this.ready$,
      this.selectedAddons$,
      this.selectedProducts$,
      this.quantities$,
      this.commitmentSelections$,
      this.o365CartLines$,
      this.domain$
    ]).pipe(
      map(([ready, selAddons, selProducts, quantities, commitments, o365CartLines, domain]) => {
        if (!ready) {
          return {
            quantity: 0,
            items: [],
            upgradeSelected: false,
            totalMonthly: 0
          } as SummaryResult;
        }

        const o365Lines = this.buildLinesFromO365Cart(o365CartLines);

        // --- Apps / Rackspace products (legacy selection model) ---
        const lines: SummaryLine[] = Array.from(selProducts)
          .map((id) => {
            const base = this.catalogById.get(id);
            const item = this.resolveO365ProductVariant(id, commitments);
            if (!item) return null;

            const unit = Number(item.price ?? 0);
            const q = quantities.get(id) || 1;
            const isO365 = base?.group === 'o365' || item.group === 'o365';

            return {
              id: item.productId,
              offerId: item.offerId,
              xmlAttributeName: item.xmlAttributeName,
              name: item.name || item.productId,
              kind: isO365 ? 'o365' : 'main',
              unitPrice: unit * q, // quantity * price
              quantity: q,
              total: unit * q,
              description: item.description,
              billingCycle: item.billingCycle === 'annual' ? 'annual' : 'monthly'
            } as SummaryLine;
          })
          .filter(Boolean) as SummaryLine[];

        const appsProductsQty = lines.reduce((sum, p) => sum + p.quantity, 0);

        // --- Addons ---
        const addons: SummaryLine[] = Array.from(selAddons)
          .map((key) => {
            if (!this.addonHasSupportingProduct(key, selProducts)) return null;

            const item = this.resolveO365Addon(key, commitments);
            if (!item) return null;

            const unit = Number(item.price ?? 0);
            const quantity = this.resolveAddonQuantity(key, item, selProducts, quantities, appsProductsQty);

            if (quantity <= 0) return null;

            return {
              id: item.parentProductId ? key : item.productId,
              offerId: item.offerId,
              xmlAttributeName: item.xmlAttributeName,
              name: item.name || item.productId,
              kind: 'addon',
              parentProductId:
                item.parentProductId ??
                this.extractParentIdFromAddonKey(key) ??
                this.resolveRackspaceAddonParent(item, selProducts),
              unitPrice: unit * quantity,
              quantity,
              total: unit * quantity,
              description: item.description,
              billingCycle: item.billingCycle === 'annual' ? 'annual' : 'monthly'
            } as SummaryLine;
          })
          .filter(Boolean) as SummaryLine[];

        // --- Domain ---
        let domainLine: SummaryLine[] = [];
        if (domain && (domain.tldPrice ?? 0) > 0) {
          const price = domain.tldPrice ?? 0;
          const years = domain.years ?? 1;
        
          domainLine = [{
            id: DOMAIN_PRODUCT_ID,
            name: domain.domainName,
            kind: 'domain',
            unitPrice: price,
            quantity: years,
            total: price * years,
            billingCycle: 'annual'
          }];
        }
        

        // Final ordered list (never show zero-qty product/add-on lines)
        const items = [...o365Lines, ...lines, ...addons, ...domainLine].filter(
          (item) => item.kind === 'domain' || item.quantity > 0
        );

        const totalProductsQty =
          o365Lines.reduce(
            (sum, line) =>
              sum + (line.kind === 'o365' || line.kind === 'addon' ? line.quantity : 0),
            0
          ) + appsProductsQty;

        const monthlyRecurring = items.reduce((sum, item) => {
          if (item.kind === 'domain' || item.billingCycle === 'annual') {
            return sum;
          }
          return sum + item.total;
        }, 0);

        const annualUpfront = items.reduce((sum, item) => {
          if (item.kind === 'domain' || item.billingCycle !== 'annual') {
            return sum;
          }
          return sum + item.total;
        }, 0);

        let chargeTotal = monthlyRecurring + annualUpfront;
        if (monthlyRecurring > 0 && annualUpfront === 0) {
          chargeTotal = Math.max(10, monthlyRecurring);
        }

        const totalMonthly = chargeTotal > 0 ? chargeTotal : 0;

        return {
          quantity: totalProductsQty,
          items,
          upgradeSelected: false,
          totalMonthly
        } as SummaryResult;
      })
    );

    this.orderItems$ = this.summary$.pipe(
      map((summary) => summary.items.map((l) => ({
        offeringId: l.id,
        productId: l.id,
        quantity: String(l.quantity),
        domainName: l.kind === 'domain' ? l.name : undefined
      })))
    );
  }

  hasInvalidQuantity(productId: string): boolean {
    return this.invalidQuantities.get(productId) || false;
  }

  getProductCommitment(productId: string): 'monthly' | 'annual' {
    return this.commitmentSelections$.value.get(productId) ?? 'monthly';
  }

  setProductCommitment(productId: string, commitment: 'monthly' | 'annual') {
    const copy = new Map(this.commitmentSelections$.value);
    copy.set(productId, commitment);
    this.commitmentSelections$.next(copy);
  }

  getActiveO365Product(product: CatalogItem): CatalogItem {
    if (
      this.getProductCommitment(product.productId) === 'annual' &&
      product.annualVariant
    ) {
      return product.annualVariant;
    }
    return product;
  }

  getActiveO365Addons(product: CatalogItem): CatalogItem[] {
    return this.getActiveO365Product(product).addons ?? [];
  }

  private resolveO365ProductVariant(
    productId: string,
    commitments: Map<string, 'monthly' | 'annual'>
  ): CatalogItem | undefined {
    const base = this.catalogById.get(productId);
    if (!base) return undefined;
    if (base.group !== 'o365') return base;

    const commitment = commitments.get(productId) ?? 'monthly';
    if (commitment === 'annual' && base.annualVariant) {
      return base.annualVariant;
    }
    return base;
  }

  private resolveO365Addon(
    key: string,
    commitments: Map<string, 'monthly' | 'annual'>
  ): CatalogItem | undefined {
    const cached = this.catalogById.get(key);
    if (!cached?.parentProductId) return cached;

    const parent = this.catalogById.get(cached.parentProductId);
    if (!parent) return cached;

    const variant = this.resolveO365ProductVariant(cached.parentProductId, commitments);
    return (
      variant?.addons?.find((addon) => addon.productId === cached.productId) ?? cached
    );
  }

  monthlyEquivalentPrice(item: CatalogItem): number {
    const price = Number(item.price ?? 0);
    return item.billingCycle === 'annual' ? price / 12 : price;
  }

  toggleAddon(productId: string, on: boolean, parentProductId?: string) {
    const key = parentProductId ? o365AddonKey(parentProductId, productId) : productId;
    const s = new Set(this.selectedAddons$.value);

    if (on) {
      s.add(key);
      const quantities = new Map(this.quantities$.value);
      if (!quantities.has(key)) {
        quantities.set(key, 1);
      }
      this.quantities$.next(quantities);
      this.validateAddonQuantity(key);
    } else {
      s.delete(key);
      const quantities = new Map(this.quantities$.value);
      quantities.delete(key);
      this.invalidQuantities.delete(key);
      this.quantities$.next(quantities);
    }

    this.selectedAddons$.next(s);
  }

  isAddonSelected(id: string, parentProductId?: string) {
    const key = parentProductId ? o365AddonKey(parentProductId, id) : id;
    return this.selectedAddons$.value.has(key);
  }

  setProductQuantity(productId: string, q: number, flow: 'apps' | 'office365' | 'reseller' | 'cloud') {
    const copy = new Map(this.quantities$.value);
    const n = Math.max(1, Number(q) || 1);

    const limit = flow === 'apps' ? 9999 : O365_MAX_LICENSES;
    this.invalidQuantities.set(productId, n > limit);

    copy.set(productId, n);
    this.quantities$.next(copy);

    if (flow === 'office365') {
      this.revalidateAddonsForParent(productId);
    }
  }

  getAddonQuantity(parentProductId: string, addonProductId: string): number {
    const key = o365AddonKey(parentProductId, addonProductId);
    return this.quantities$.value.get(key) ?? 1;
  }

  getAddonMaxQuantity(parentProductId: string, addonProductId: string): number {
    return this.computeAddonMaxQuantity(o365AddonKey(parentProductId, addonProductId));
  }

  hasInvalidAddonQuantity(parentProductId: string, addonProductId: string): boolean {
    const key = o365AddonKey(parentProductId, addonProductId);
    return this.invalidQuantities.get(key) || false;
  }

  hasAnyInvalidAddonQuantity(): boolean {
    for (const key of this.selectedAddons$.value) {
      if (this.invalidQuantities.get(key)) {
        return true;
      }
    }
    return false;
  }

  setAddonQuantity(parentProductId: string, addonProductId: string, q: number) {
    const key = o365AddonKey(parentProductId, addonProductId);
    const copy = new Map(this.quantities$.value);
    const n = Math.max(1, Number(q) || 1);
    copy.set(key, n);
    this.quantities$.next(copy);
    this.validateAddonQuantity(key);
  }

  toggleProduct(productId: string, on: boolean) {
    const s = new Set(this.selectedProducts$.value);
    const item = this.catalogById.get(productId);

    if (on) {
      if (item && item.group === 'rackspace' && item.type === 'product') {
        Array.from(s).forEach((id) => {
          const it = this.catalogById.get(id);
          if (it && it.group === 'rackspace' && it.type === 'product') {
            s.delete(id);
          }
        });
      }
      s.add(productId);
    } else {
      s.delete(productId);
    }

    this.selectedProducts$.next(s);

    if (!on) {
      this.pruneOrphanedAddons(s);
    }
  }

  isSelected(productId: string): boolean {
    return (
      this.isProductSelected(productId) ||
      this.isAddonSelected(productId)
    );
  }

  isProductSelected(id: string) {
    return this.selectedProducts$.value.has(id);
  }

  getSelectedProductCount(): number {
    return this.selectedProducts$.value.size + this.o365CartLines$.value.length;
  }

  getO365CartLineCount(): number {
    return this.o365CartLines$.value.length;
  }

  getO365TotalLicenseQuantity(): number {
    return this.o365CartLines$.value.reduce(
      (sum, line) => sum + getO365CartLineLicenseQuantity(line),
      0
    );
  }

  hasO365TotalLicenseLimitExceeded(): boolean {
    return this.getO365TotalLicenseQuantity() > O365_MAX_LICENSES;
  }

  wouldExceedO365LicenseLimit(
    quantity: number,
    excludeLineId?: string,
    addons: O365CartAddon[] = []
  ): boolean {
    const proposedLineTotal = getO365CartLicenseQuantity(quantity, addons);
    const currentTotal = this.o365CartLines$.value
      .filter((line) => line.lineId !== excludeLineId)
      .reduce((sum, line) => sum + getO365CartLineLicenseQuantity(line), 0);

    return currentTotal + proposedLineTotal > O365_MAX_LICENSES;
  }

  get o365CartLinesChanges$(): Observable<O365CartLine[]> {
    return this.o365CartLines$.asObservable();
  }

  getO365CartLinesForProduct(catalogProductId: string): O365CartLine[] {
    return this.o365CartLines$.value.filter(
      (line) => line.catalogProductId === catalogProductId
    );
  }

  getO365CartLineForProduct(catalogProductId: string): O365CartLine | undefined {
    return this.o365CartLines$.value.find(
      (line) => line.catalogProductId === catalogProductId
    );
  }

  getO365EligibleParentQuantityForAddon(
    addonProductId: string,
    options?: {
      replaceCatalogProductId?: string;
      replaceQuantity?: number;
      excludeLineId?: string;
    }
  ): number {
    const supportedBy = this.getO365AddonEligibleParents(addonProductId);
    if (!supportedBy.size) {
      return 0;
    }

    let total = 0;
    for (const line of this.o365CartLines$.value) {
      if (options?.excludeLineId && line.lineId === options.excludeLineId) {
        continue;
      }
      if (
        options?.replaceCatalogProductId &&
        line.catalogProductId === options.replaceCatalogProductId
      ) {
        continue;
      }
      if (supportedBy.has(line.catalogProductId)) {
        total += line.quantity;
      }
    }

    if (
      options?.replaceCatalogProductId &&
      supportedBy.has(options.replaceCatalogProductId)
    ) {
      total += Math.max(0, options.replaceQuantity ?? 0);
    }

    return total;
  }

  getO365CartAddonQuantityForProduct(
    addonProductId: string,
    options?: {
      excludeLineId?: string;
      replaceLineId?: string;
      replaceCatalogProductId?: string;
      replaceDraftAddons?: O365CartAddon[];
    }
  ): number {
    const aggregateKey = this.getO365AddonAggregateKey(addonProductId);
    let total = 0;

    for (const line of this.o365CartLines$.value) {
      if (options?.replaceLineId && line.lineId === options.replaceLineId) {
        total += (options.replaceDraftAddons ?? [])
          .filter(
            (addon) => this.getO365AddonAggregateKey(addon.addonProductId) === aggregateKey
          )
          .reduce((sum, addon) => sum + addon.quantity, 0);
        continue;
      }
      if (options?.excludeLineId && line.lineId === options.excludeLineId) {
        continue;
      }
      if (
        options?.replaceCatalogProductId &&
        line.catalogProductId === options.replaceCatalogProductId
      ) {
        continue;
      }

      for (const addon of line.addons) {
        if (this.getO365AddonAggregateKey(addon.addonProductId) === aggregateKey) {
          total += addon.quantity;
        }
      }
    }

    if (
      options?.replaceCatalogProductId &&
      options.replaceDraftAddons &&
      !options.replaceLineId
    ) {
      total += options.replaceDraftAddons
        .filter(
          (addon) => this.getO365AddonAggregateKey(addon.addonProductId) === aggregateKey
        )
        .reduce((sum, addon) => sum + addon.quantity, 0);
    }

    return total;
  }

  hasO365CartAddonProductQuantityExceeded(addonProductId: string): boolean {
    const aggregateKey = this.getO365AddonAggregateKey(addonProductId);
    return this.getO365AddonLimitViolations(this.o365CartLines$.value).has(aggregateKey);
  }

  getO365CartAddonMaxQuantity(lineId: string, addonProductId: string): number {
    const line = this.o365CartLines$.value.find((entry) => entry.lineId === lineId);
    if (!line) {
      return 1;
    }

    const aggregateKey = this.getO365AddonAggregateKey(addonProductId);
    const eligible = this.getO365EligibleParentQuantityForAddon(addonProductId);
    const total = this.getO365CartAddonQuantityForProduct(addonProductId);
    const current = line.addons
      .filter((addon) => this.getO365AddonAggregateKey(addon.addonProductId) === aggregateKey)
      .reduce((sum, addon) => sum + addon.quantity, 0);
    const remaining = Math.max(0, eligible - (total - current));

    return Math.max(1, Math.min(line.quantity, remaining));
  }

  wouldExceedO365AddonLimitsForDraft(
    catalogProductId: string,
    parentQuantity: number,
    draftAddons: O365CartAddon[],
    excludeLineId?: string
  ): boolean {
    const aggregateKeys = new Set<string>();
    for (const addon of draftAddons) {
      aggregateKeys.add(this.getO365AddonAggregateKey(addon.addonProductId));
    }
    for (const line of this.o365CartLines$.value) {
      if (excludeLineId && line.lineId === excludeLineId) {
        continue;
      }
      for (const addon of line.addons) {
        aggregateKeys.add(this.getO365AddonAggregateKey(addon.addonProductId));
      }
    }

    for (const aggregateKey of aggregateKeys) {
      const representativeAddonId = this.getRepresentativeAddonProductId(aggregateKey);
      if (!representativeAddonId) {
        continue;
      }

      const eligible = this.getO365EligibleParentQuantityForAddon(representativeAddonId, {
        replaceCatalogProductId: catalogProductId,
        replaceQuantity: parentQuantity,
        excludeLineId,
      });
      const total = excludeLineId
        ? this.getO365CartAddonQuantityForProduct(representativeAddonId, {
            replaceLineId: excludeLineId,
            replaceDraftAddons: draftAddons,
          })
        : this.getO365CartAddonQuantityForProduct(representativeAddonId, {
            replaceCatalogProductId: catalogProductId,
            replaceDraftAddons: draftAddons,
          });

      if (total > eligible) {
        return true;
      }
    }

    return false;
  }

  private getO365AddonLimitViolations(lines: O365CartLine[]): Set<string> {
    const violated = new Set<string>();
    const totalsByAggregateKey = new Map<string, number>();

    for (const line of lines) {
      for (const addon of line.addons) {
        const aggregateKey = this.getO365AddonAggregateKey(addon.addonProductId);
        totalsByAggregateKey.set(
          aggregateKey,
          (totalsByAggregateKey.get(aggregateKey) ?? 0) + addon.quantity
        );
      }
    }

    for (const [aggregateKey, total] of totalsByAggregateKey) {
      const supportedBy = this.addonAggregateSupportedBy.get(aggregateKey);
      if (!supportedBy?.size) {
        continue;
      }

      let eligible = 0;
      for (const line of lines) {
        if (supportedBy.has(line.catalogProductId)) {
          eligible += line.quantity;
        }
      }

      if (eligible > 0 && total > eligible) {
        violated.add(aggregateKey);
      }
    }

    return violated;
  }

  private getO365AddonEligibleParents(addonProductId: string): Set<string> {
    const aggregateKey = this.getO365AddonAggregateKey(addonProductId);
    const aggregateParents = this.addonAggregateSupportedBy.get(aggregateKey);
    if (aggregateParents?.size) {
      return new Set(aggregateParents);
    }

    return new Set(this.addonSupportedBy.get(addonProductId) ?? []);
  }

  private getRepresentativeAddonProductId(aggregateKey: string): string | undefined {
    return this.addonProductIdsByAggregateKey.get(aggregateKey)?.values().next().value;
  }

  private resolveO365AddonCatalogItem(addonProductId: string): CatalogItem | undefined {
    const direct = this.catalogById.get(addonProductId);
    if (direct) {
      return direct;
    }

    for (const item of this.catalogById.values()) {
      if (item.productId === addonProductId && item.group === 'o365-addon') {
        return item;
      }
    }

    return undefined;
  }

  private getO365AddonAggregateKey(addonProductId: string): string {
    return this.getO365AddonAggregateKeyFromItem(
      this.resolveO365AddonCatalogItem(addonProductId),
      addonProductId
    );
  }

  private getO365AddonAggregateKeyFromItem(
    item: CatalogItem | undefined,
    addonProductId: string
  ): string {
    if (item?.externalOfferId) {
      return `offer:${item.externalOfferId}`;
    }
    if (item?.serviceCode != null) {
      return `service:${item.serviceCode}`;
    }
    return `product:${addonProductId}`;
  }

  private registerO365AddonSupport(addon: CatalogItem, parentProductId: string): void {
    if (!this.addonSupportedBy.has(addon.productId)) {
      this.addonSupportedBy.set(addon.productId, new Set());
    }
    this.addonSupportedBy.get(addon.productId)!.add(parentProductId);

    const aggregateKey = this.getO365AddonAggregateKeyFromItem(addon, addon.productId);
    if (!this.addonAggregateSupportedBy.has(aggregateKey)) {
      this.addonAggregateSupportedBy.set(aggregateKey, new Set());
      this.addonProductIdsByAggregateKey.set(aggregateKey, new Set());
    }
    this.addonAggregateSupportedBy.get(aggregateKey)!.add(parentProductId);
    this.addonProductIdsByAggregateKey.get(aggregateKey)!.add(addon.productId);
  }

  addO365CartLine(
    catalogProductId: string,
    commitment: 'monthly' | 'annual',
    quantity: number,
    addons: O365CartAddon[] = []
  ): boolean {
    const q = Math.max(1, Number(quantity) || 1);
    const normalizedAddons = normalizeO365CartAddons(addons);

    const existingLine = this.o365CartLines$.value.find(
      (line) => line.catalogProductId === catalogProductId
    );
    const excludeLineId = existingLine?.lineId;

    if (
      q > O365_MAX_LICENSES ||
      this.wouldExceedO365LicenseLimit(q, excludeLineId, normalizedAddons) ||
      this.wouldExceedO365AddonLimitsForDraft(
        catalogProductId,
        q,
        normalizedAddons,
        excludeLineId
      )
    ) {
      return false;
    }

    let nextLines = this.o365CartLines$.value.filter(
      (line) => line.catalogProductId !== catalogProductId
    );

    if (existingLine) {
      nextLines = [
        ...nextLines,
        {
          ...existingLine,
          commitment,
          quantity: q,
          addons: normalizedAddons,
        },
      ];
    } else {
      this.o365LineCounter += 1;
      const lineId = `o365-line-${this.o365LineCounter}`;
      nextLines = [
        ...nextLines,
        {
          lineId,
          catalogProductId,
          commitment,
          quantity: q,
          addons: normalizedAddons,
        },
      ];
    }

    this.o365CartLines$.next(this.dedupeO365CartLines(nextLines));
    this.revalidateO365CartQuantities();
    return true;
  }

  hasO365CartLineAddonQuantityExceeded(lineId: string): boolean {
    const line = this.o365CartLines$.value.find((entry) => entry.lineId === lineId);
    if (!line) {
      return false;
    }

    const violations = this.getO365AddonLimitViolations(this.o365CartLines$.value);
    return line.addons.some((addon) =>
      violations.has(this.getO365AddonAggregateKey(addon.addonProductId))
    );
  }

  hasAnyO365CartLineAddonQuantityExceeded(): boolean {
    return this.getO365AddonLimitViolations(this.o365CartLines$.value).size > 0;
  }

  getO365CartLineAddonQuantityTotal(lineId: string): number {
    const line = this.o365CartLines$.value.find((entry) => entry.lineId === lineId);
    return line ? getO365CartLineAddonQuantityTotal(line) : 0;
  }

  groupO365SummaryAddons(addons: SummaryLine[]): O365AddonGroup<SummaryLine>[] {
    return groupO365SummaryAddonLines(addons, (addonProductId) =>
      this.resolveO365AddonCatalogItem(addonProductId)
    );
  }

  private dedupeO365CartLines(lines: O365CartLine[]): O365CartLine[] {
    const merged = new Map<string, O365CartLine>();

    for (const line of lines) {
      const signature = o365CartLineSignature(
        line.catalogProductId,
        line.commitment,
        line.addons
      );
      const existing = merged.get(signature);

      if (!existing) {
        merged.set(signature, line);
        continue;
      }

      merged.set(signature, {
        ...existing,
        quantity: existing.quantity + line.quantity,
      });
    }

    return Array.from(merged.values());
  }

  removeO365CartLine(lineId: string) {
    this.o365CartLines$.next(
      this.o365CartLines$.value.filter((line) => line.lineId !== lineId)
    );
    this.invalidQuantities.delete(lineId);
    this.revalidateO365CartQuantities();
  }

  setO365CartLineQuantity(lineId: string, quantity: number) {
    const q = Math.max(1, Number(quantity) || 1);
    const line = this.o365CartLines$.value.find((entry) => entry.lineId === lineId);
    const addons = line?.addons ?? [];
    this.invalidQuantities.set(
      lineId,
      q > O365_MAX_LICENSES || this.wouldExceedO365LicenseLimit(q, lineId, addons)
    );

    const updated = this.o365CartLines$.value.map((line) => {
      if (line.lineId !== lineId) return line;
      return {
        ...line,
        quantity: q,
      };
    });

    this.o365CartLines$.next(updated);
    this.revalidateO365CartQuantities();
  }

  removeO365CartAddon(lineId: string, addonProductId: string) {
    const updated = this.o365CartLines$.value.map((line) => {
      if (line.lineId !== lineId) return line;
      return {
        ...line,
        addons: line.addons.filter((addon) => addon.addonProductId !== addonProductId),
      };
    });

    this.o365CartLines$.next(updated);
  }

  setO365CartAddonQuantity(lineId: string, addonProductId: string, quantity: number) {
    if (!Number.isFinite(quantity) || quantity < 1) {
      return;
    }

    const updated = this.o365CartLines$.value.map((line) => {
      if (line.lineId !== lineId) return line;
      const q = Math.floor(quantity);

      return {
        ...line,
        addons: line.addons.map((addon) =>
          addon.addonProductId === addonProductId ? { ...addon, quantity: q } : addon
        ),
      };
    });

    this.o365CartLines$.next(updated);
    this.revalidateO365CartQuantities();
  }

  private revalidateO365CartQuantities() {
    const lines = this.o365CartLines$.value;
    const addonViolations = this.getO365AddonLimitViolations(lines);
    const totalExceeded = this.hasO365TotalLicenseLimitExceeded();

    for (const line of lines) {
      const perLineExceeded = line.quantity > O365_MAX_LICENSES;
      const wouldExceed = this.wouldExceedO365LicenseLimit(
        line.quantity,
        line.lineId,
        line.addons
      );
      const addonLimitExceeded = line.addons.some((addon) =>
        addonViolations.has(this.getO365AddonAggregateKey(addon.addonProductId))
      );
      this.invalidQuantities.set(
        line.lineId,
        perLineExceeded || wouldExceed || totalExceeded || addonLimitExceeded
      );
    }
  }

  private sortO365CartAddons(
    cartAddons: O365CartAddon[],
    catalogAddons: CatalogItem[]
  ): O365CartAddon[] {
    const catalogByProductId = new Map(
      catalogAddons.map((addon) => [addon.productId, addon])
    );

    return sortByO365AddonGroups(
      cartAddons,
      (entry) => entry.addonProductId,
      (addonProductId) => catalogByProductId.get(addonProductId)
    );
  }

  private buildLinesFromO365Cart(cartLines: O365CartLine[]): SummaryLine[] {
    const result: SummaryLine[] = [];

    for (const cartLine of cartLines) {
      const base = this.catalogById.get(cartLine.catalogProductId);
      if (!base) continue;

      const item =
        cartLine.commitment === 'annual' && base.annualVariant
          ? base.annualVariant
          : base;
      const unit = Number(item.price ?? 0);
      const q = cartLine.quantity;

      result.push({
        id: item.productId,
        cartLineId: cartLine.lineId,
        offerId: item.offerId,
        xmlAttributeName: item.xmlAttributeName,
        name: item.name || item.productId,
        kind: 'o365',
        unitPrice: unit * q,
        quantity: q,
        total: unit * q,
        description: item.description,
        billingCycle: item.billingCycle === 'annual' ? 'annual' : 'monthly',
        commitment: cartLine.commitment,
      });

      for (const cartAddon of this.sortO365CartAddons(cartLine.addons, item.addons ?? [])) {
        const addonCatalog = item.addons?.find(
          (addon) => addon.productId === cartAddon.addonProductId
        );
        if (!addonCatalog) continue;

        const addonUnit = Number(addonCatalog.price ?? 0);
        const addonQty = Math.min(Math.max(1, cartAddon.quantity), q);

        result.push({
          id: o365AddonKey(cartLine.lineId, cartAddon.addonProductId),
          cartLineId: cartLine.lineId,
          addonProductId: cartAddon.addonProductId,
          offerId: addonCatalog.offerId,
          xmlAttributeName: addonCatalog.xmlAttributeName,
          name: addonCatalog.name || addonCatalog.productId,
          kind: 'addon',
          parentProductId: cartLine.lineId,
          unitPrice: addonUnit * addonQty,
          quantity: addonQty,
          total: addonUnit * addonQty,
          description: addonCatalog.description,
          billingCycle: addonCatalog.billingCycle === 'annual' ? 'annual' : 'monthly',
        });
      }
    }

    return result;
  }

  setDomain(domain: DomainSelection | null) {
    this.domain$.next(domain);
  }

  clearAll() {
    this.selectedAddons$.next(new Set());
    this.selectedProducts$.next(new Set());
    this.quantities$.next(new Map());
    this.commitmentSelections$.next(new Map());
    this.o365CartLines$.next([]);
    this.o365LineCounter = 0;
    this.domain$.next(null);
    this.invalidQuantities.clear();
  }

  getQuantity(productId: string): number {
    return this.quantities$.value.get(productId) || 1;
  }

  private registerO365ProductAddons(product: CatalogItem, parentProductId = product.productId) {
    (product.addons ?? []).forEach((addon) => {
      this.registerO365AddonSupport(addon, parentProductId);
      this.catalogById.set(o365AddonKey(parentProductId, addon.productId), addon);
    });
  }

  private resolveAddonQuantity(
    key: string,
    item: CatalogItem,
    selectedProducts: Set<string>,
    quantities: Map<string, number>,
    totalProductsQty: number
  ): number {
    const max = this.computeAddonMaxQuantity(key, selectedProducts, quantities);
    if (max <= 0) return 0;

    if (this.isIndependentO365Addon(key, item)) {
      return quantities.get(key) ?? 1;
    }

    return totalProductsQty;
  }

  private isIndependentO365Addon(key: string, item: CatalogItem): boolean {
    if (item.group === 'addon') {
      return false;
    }

    return (
      key.includes('::') ||
      item.group === 'o365-addon' ||
      this.addonSupportedBy.has(item.productId)
    );
  }

  private computeAddonMaxQuantity(
    key: string,
    selectedProducts = this.selectedProducts$.value,
    quantities = this.quantities$.value
  ): number {
    const separatorIndex = key.indexOf('::');
    if (separatorIndex >= 0) {
      const parentId = key.slice(0, separatorIndex);
      return selectedProducts.has(parentId) ? quantities.get(parentId) || 1 : 0;
    }

    const supportedBy = this.addonSupportedBy.get(key);
    if (!supportedBy?.size) {
      return 0;
    }

    return Array.from(supportedBy).reduce((sum, parentId) => {
      if (!selectedProducts.has(parentId)) return sum;
      return sum + (quantities.get(parentId) || 1);
    }, 0);
  }

  private validateAddonQuantity(key: string) {
    const max = this.computeAddonMaxQuantity(key);
    const qty = this.quantities$.value.get(key) ?? 1;
    this.invalidQuantities.set(key, max > 0 && qty > max);
  }

  private revalidateAddonsForParent(parentProductId: string) {
    for (const key of this.selectedAddons$.value) {
      if (key.startsWith(`${parentProductId}::`)) {
        this.validateAddonQuantity(key);
      }
    }

    for (const [addonId, parents] of this.addonSupportedBy.entries()) {
      if (parents.has(parentProductId) && this.selectedAddons$.value.has(addonId)) {
        this.validateAddonQuantity(addonId);
      }
    }
  }

  private extractParentIdFromAddonKey(key: string): string | undefined {
    const separatorIndex = key.indexOf('::');
    return separatorIndex >= 0 ? key.slice(0, separatorIndex) : undefined;
  }

  private resolveRackspaceAddonParent(
    item: CatalogItem,
    selectedProducts: Set<string>
  ): string | undefined {
    if (item.group !== 'addon') {
      return undefined;
    }

    const supportedBy = this.addonSupportedBy.get(item.productId);
    if (!supportedBy?.size) {
      return undefined;
    }

    for (const parentId of supportedBy) {
      if (selectedProducts.has(parentId)) {
        return parentId;
      }
    }

    return undefined;
  }

  private addonHasSupportingProduct(key: string, selectedProducts: Set<string>): boolean {
    return this.computeAddonMaxQuantity(key, selectedProducts) > 0;
  }

  /** Removes add-ons that no longer have any selected supporting base product. */
  private pruneOrphanedAddons(selectedProducts: Set<string>) {
    const addons = new Set(this.selectedAddons$.value);
    let changed = false;

    for (const key of Array.from(addons)) {
      if (!this.addonHasSupportingProduct(key, selectedProducts)) {
        addons.delete(key);
        changed = true;
      }
    }

    if (changed) {
      const quantities = new Map(this.quantities$.value);
      for (const key of Array.from(quantities.keys())) {
        const isNestedAddon = key.includes('::');
        const isFlatO365Addon = this.addonSupportedBy.has(key);
        if ((isNestedAddon || isFlatO365Addon) && !addons.has(key)) {
          quantities.delete(key);
          this.invalidQuantities.delete(key);
        }
      }
      this.quantities$.next(quantities);
      this.selectedAddons$.next(addons);
    }
  }
}
